/**
 * HallWalkControls — the input layer for the waiting-room "walk around the
 * hall + push other players" feature.
 *
 * The 3D backdrop ({@link RoomBackdrop3DScene}) sits in a `z-index:-1;
 * pointer-events:none` layer, so it can't take keyboard focus or touches. This
 * component lives in the normal Room UI and:
 *
 *  - DESKTOP: listens for WASD / arrow keys at the document level and writes a
 *    live `{x,z}` direction into a ref the backdrop reads each frame. It IGNORES
 *    movement keys while a text input / textarea / contenteditable is focused so
 *    it never steals the chat composer's typing (a WASD in chat must type, not
 *    walk).
 *  - TOUCH: renders a small virtual joystick pinned bottom-left (only when no
 *    modal/panel is open), which drives the same ref.
 *
 * It owns the `inputRef` and returns it (+ a joystick overlay element) so Room
 * can hand the ref to <RoomBackdrop3D> without re-rendering on every keypress.
 * Movement itself is user-initiated, so it stays active under reduced-motion
 * (only ambient hall animation freezes).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { MoveInput } from './RoomBackdrop3DScene';

const MOVE_KEYS: Record<string, { x: number; z: number }> = {
  KeyW: { x: 0, z: 1 }, ArrowUp: { x: 0, z: 1 },
  KeyS: { x: 0, z: -1 }, ArrowDown: { x: 0, z: -1 },
  KeyA: { x: -1, z: 0 }, ArrowLeft: { x: -1, z: 0 },
  KeyD: { x: 1, z: 0 }, ArrowRight: { x: 1, z: 0 },
};

/** True when focus is in a field where typing must NOT drive movement. */
function typingTarget(el: EventTarget | null): boolean {
  const n = el as HTMLElement | null;
  if (!n) return false;
  const tag = n.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || n.isContentEditable;
}

export interface HallWalkControls {
  /** Hand this to <RoomBackdrop3D inputRef={...} />. */
  inputRef: React.MutableRefObject<MoveInput | null>;
  /** The touch joystick overlay (render it inside the Room). Null on desktop. */
  joystick: React.ReactNode;
}

/**
 * @param enabled  Turn the whole thing off (e.g. a modal/panel is open) — clears
 *                 input + hides the joystick so movement never fights a dialog.
 */
export function useHallWalk(enabled: boolean): HallWalkControls {
  const inputRef = useRef<MoveInput | null>({ x: 0, z: 0 });
  // Which movement keys are currently held (desktop). Recomputes the ref vector.
  const held = useRef<Set<string>>(new Set());
  const [isTouch] = useState(() =>
    typeof window !== 'undefined' && (('ontouchstart' in window) || (navigator.maxTouchPoints ?? 0) > 0),
  );

  // Keep a live ref of `enabled` so the once-mounted key listeners see it.
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const recomputeFromKeys = () => {
    let x = 0;
    let z = 0;
    for (const code of held.current) {
      const v = MOVE_KEYS[code];
      if (v) { x += v.x; z += v.z; }
    }
    const mag = Math.hypot(x, z);
    inputRef.current = mag > 0 ? { x: x / mag, z: z / mag } : { x: 0, z: 0 };
  };

  useEffect(() => {
    if (!enabled) {
      held.current.clear();
      inputRef.current = { x: 0, z: 0 };
    }
  }, [enabled]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!enabledRef.current) return;
      if (!(e.code in MOVE_KEYS)) return;
      if (typingTarget(e.target) || typingTarget(document.activeElement)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      e.preventDefault(); // stop arrow keys from scrolling the room
      held.current.add(e.code);
      recomputeFromKeys();
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (!(e.code in MOVE_KEYS)) return;
      held.current.delete(e.code);
      recomputeFromKeys();
    };
    // If focus leaves the tab, drop all held keys (no stuck-walking).
    const onBlur = () => { held.current.clear(); recomputeFromKeys(); };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
    };
  }, []);

  const joystick = useMemo(
    () => (isTouch && enabled ? <Joystick inputRef={inputRef} /> : null),
    [isTouch, enabled],
  );

  return { inputRef, joystick };
}

/**
 * A minimal thumb-stick: a base disc + a draggable nub. Pointer position within
 * the base maps to a clamped -1..1 {x,z} written into `inputRef` (z is inverted
 * so "up" = away-from-camera, matching the scene's screen→world mapping). Pointer
 * capture means the drag keeps tracking even if the finger leaves the disc.
 */
function Joystick({ inputRef }: { inputRef: React.MutableRefObject<MoveInput | null> }) {
  const baseRef = useRef<HTMLDivElement | null>(null);
  const [nub, setNub] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const activeId = useRef<number | null>(null);
  const RADIUS = 42; // px travel of the nub before saturating

  const update = (clientX: number, clientY: number) => {
    const base = baseRef.current;
    if (!base) return;
    const r = base.getBoundingClientRect();
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    let dx = clientX - cx;
    let dy = clientY - cy;
    const d = Math.hypot(dx, dy) || 1;
    const clamped = Math.min(d, RADIUS);
    dx = (dx / d) * clamped;
    dy = (dy / d) * clamped;
    setNub({ x: dx, y: dy });
    // Screen y-down → world "up"/away is -y, so z = -dy/RADIUS.
    inputRef.current = { x: dx / RADIUS, z: -dy / RADIUS };
  };
  const end = () => {
    activeId.current = null;
    setNub({ x: 0, y: 0 });
    inputRef.current = { x: 0, z: 0 };
  };

  return (
    <div
      className="hall-joystick"
      ref={baseRef}
      aria-hidden="true"
      onPointerDown={(e) => {
        activeId.current = e.pointerId;
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
        update(e.clientX, e.clientY);
      }}
      onPointerMove={(e) => {
        if (activeId.current !== e.pointerId) return;
        update(e.clientX, e.clientY);
      }}
      onPointerUp={(e) => { if (activeId.current === e.pointerId) end(); }}
      onPointerCancel={() => end()}
    >
      <span className="hall-joystick-nub" style={{ transform: `translate(${nub.x}px, ${nub.y}px)` }} />
    </div>
  );
}
